import { Button } from "@/components/core/buttons/button";
import { JournalAccountSelect } from "@/components/invoices/journal-account-select";
import { journalAccountSettingsQueryOptions, useUpdateJournalAccountSettingsMutation } from "@/features/dashboard/api/queries/journal-accounts-query";
import { JOURNAL_ACCOUNT_PURPOSE_LABELS, type JournalAccountPurpose } from "@/features/dashboard/types/chart-of-accounts";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

export const Route = createFileRoute("/_dashboard/journal-accounts")({
	component: JournalAccountsSettingsPage,
});

function JournalAccountsSettingsPage() {
	const settingsQuery = useQuery(journalAccountSettingsQueryOptions());
	const updateMutation = useUpdateJournalAccountSettingsMutation();

	const [drafts, setDrafts] = useState<Record<string, string>>({});

	useEffect(() => {
		if (settingsQuery.data) {
			const next: Record<string, string> = {};
			for (const s of settingsQuery.data) {
				next[s.purpose] = s.account_code;
			}
			setDrafts(next);
		}
	}, [settingsQuery.data]);

	const dirty = settingsQuery.data?.some((s) => drafts[s.purpose] !== s.account_code) ?? false;

	const handleSave = () => {
		const payload = Object.entries(drafts).map(([purpose, account_code]) => ({
			purpose: purpose as JournalAccountPurpose,
			account_code,
		}));
		updateMutation.mutate(payload);
	};

	return (
		<div className="container mx-auto px-4 py-6 max-w-3xl">
			<div className="mb-6">
				<h1 className="text-3xl font-bold text-gray-900">Journal Account Defaults</h1>
				<p className="text-gray-600 mt-2">
					Map each accounting purpose to a Chart of Accounts entry from Books. These defaults are used for every invoice's draft JE; individual invoices can
					still override per-line.
				</p>
			</div>

			{settingsQuery.isLoading ? (
				<div className="py-10 text-center text-sm text-gray-500">Loading…</div>
			) : (
				<div className="rounded-lg border border-gray-200 bg-white">
					<table className="w-full text-sm">
						<thead className="border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
							<tr>
								<th className="py-3 px-4 w-[40%]">Purpose</th>
								<th className="py-3 px-4">Books Account</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-gray-100">
							{settingsQuery.data?.map((s) => (
								<tr key={s.purpose}>
									<td className="py-3 px-4 font-medium text-gray-900">{JOURNAL_ACCOUNT_PURPOSE_LABELS[s.purpose] ?? s.purpose}</td>
									<td className="py-3 px-4">
										<JournalAccountSelect
											value={drafts[s.purpose] ?? s.account_code}
											onChange={(code, _name) => setDrafts((prev) => ({ ...prev, [s.purpose]: code }))}
										/>
									</td>
								</tr>
							))}
						</tbody>
					</table>
					<div className="flex items-center justify-end gap-2 border-t border-gray-200 bg-gray-50 px-4 py-3">
						<Button variant="ghost" size="sm" disabled={!dirty} onClick={() => settingsQuery.refetch()}>
							Reset
						</Button>
						<Button size="sm" disabled={!dirty || updateMutation.isPending} onClick={handleSave}>
							{updateMutation.isPending ? "Saving…" : "Save defaults"}
						</Button>
					</div>
				</div>
			)}
		</div>
	);
}
